import type { TrackingEvent, TrackingPayload } from './types'
import { mapProductToTrackingPayload, enrichTrackingData } from './helpers'
import {
  useBasketEvents,
  useShopEvents,
  useWishlistEvents,
  useProductEvents,
  useCheckoutEvents,
  useSearchEvents,
  usePurchaseEvents,
  usePromotionEvents,
  useUserActionEvents,
  useCustomEvents,
  useYouTubeEvents,
  useNavigationEvents,
  useBlogEvents,
  useOdoscopeEvents,
  useExperimentsEvents,
} from './events'
import { useState } from '#app'

/**
 * Higher order composable which serves as a single entry point to unify different ecommerce tracking events
 */
const useTrackingEvents = (useServerQueue?: boolean) => {
  let $tracking: any = null
  let route: any = null

  try {
    const nuxtApp = useNuxtApp()
    $tracking = nuxtApp.$tracking
    route = useRoute()
  } catch (error) {
    console.error('Nuxt instance unavailable in useTrackingEvents:', error)
    // Provide fallback route object
    route = { path: '', query: {}, params: {} }
  }

  const serverQueue = useState<any[]>('serverQueue', () => [])

  // Returns whether the event was actually pushed/enqueued. Callers that dedup
  // on a successful send (e.g. carousel impression tracking) rely on this so a
  // not-ready `$tracking` doesn't permanently drop an impression.
  const track = (event: TrackingEvent, payload: TrackingPayload): boolean => {
    try {
      const trackingData = enrichTrackingData(event, payload, route)
      if (import.meta.server) {
        if (useServerQueue) {
          serverQueue.value = [...serverQueue.value, trackingData]
          return true
        }
        return false
      }
      // Guard: if Nuxt app or $tracking isn't ready yet, no-op instead of throwing
      try {
        const nuxt =
          typeof useNuxtApp === 'function' ? (useNuxtApp() as any) : null
        const tracker = nuxt?.$tracking
        if (!tracker?.push) {
          console.error(
            `Tracking data not pushed for event: ${event}`,
            trackingData,
          )
          return false
        }
        tracker.push(trackingData)
        return true
      } catch (error) {
        console.error(`Error pushing tracking data for event: ${event}`, error)
        return false
      }
    } catch (error) {
      console.error(`Error tracking event: ${event}`, error)
      return false
    }
  }

  const fireTrackingServerQueue = () => {
    try {
      if (useServerQueue && $tracking?.push) {
        serverQueue.value.map((trackingData) => $tracking.push(trackingData))
        serverQueue.value = []
      }
    } catch (error) {
      console.error('Error firing tracking server queue:', error)
    }
  }

  /**
   * NOTE: Important considerations:
   * sub-composable(s) such as useBasketEvents, etc should not be utilized directly
   * instead make use of the higher order composable (useTrackingEvents) to keep things clean
   * new events should be added to relevant sub-composable to ensure DRY code and separation of concerns
   */

  return {
    ...useShopEvents(track),
    ...useBasketEvents(track),
    ...useWishlistEvents(track),
    ...useProductEvents(track),
    ...useCheckoutEvents(track),
    ...useSearchEvents(track),
    ...usePurchaseEvents(track, { $tracking }),
    ...usePromotionEvents(track),
    ...useUserActionEvents(track, { $tracking }),
    ...useCustomEvents(track),
    ...useYouTubeEvents(track),
    ...useNavigationEvents(track),
    ...useBlogEvents(track),
    ...useOdoscopeEvents(track),
    ...useExperimentsEvents(track),
    mapProductToTrackingPayload,
    fireTrackingServerQueue,
  }
}

export default useTrackingEvents
